/**
 * v4.36.0 — Daily Portfolio Review — Mission Control (Phase 1b).
 *
 * Phase 1b changes vs 1a:
 *   • Subtitles shifted to past tense — "Here's what happened yesterday
 *     across your portfolio." Mission Control is a briefing, not a
 *     dashboard. Humans think in days.
 *   • `top_performer` / `top_organization` cards render runners-up
 *     (#2 / #3) as a small subtitle line beneath the winner. Top 3 only;
 *     no scroll, no paging.
 *   • `needs_attention` is now a tagged union over three signals:
 *       - unresolved_feedback (recovery work, threshold ≥3 cases)
 *       - rating_drop        (★ slip ≥0.3 with sample ≥10 reviews)
 *       - tap_drop           (volume ↓ ≥40% with last-week sample ≥20)
 *     The frontend simply branches on `event.signal` and reads
 *     signal-specific i18n strings.
 *   • All zero-value tiles are suppressed server-side — Mission Control
 *     never shows dead readouts.
 *
 * Visual identity unchanged: imports tokens from
 *   /app/frontend/src/styles/mission-control-tokens.css
 * Event-driven renderer is preserved — adding a new card means adding
 * a new switch case + i18n keys. Future Timeline view (Phase 3) reads
 * the same `events` array.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Sparkles, TrendingUp, Star, MessageSquare, Trophy, AlertTriangle,
    Megaphone, Rocket, Home, BarChart3, ArrowRight, ArrowUpRight,
    ArrowDownRight, DollarSign, Users, Loader2, Clock, Globe2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { welcomeAPI, preferencesAPI } from '../lib/api';
import { canSeeOperationalBase } from '../lib/obVisibility';

// Mission Control safety nets. The daily-review payload is an
// occasionally-heavy cross-location aggregation; a cold super-admin /
// portfolio build can take several seconds. These guards ensure the
// briefing NEVER traps the user on a bare spinner (the intermittent
// "stuck on Welcome after login" report):
//   • WELCOME_FETCH_TIMEOUT_MS — abort a slow/hung request and show the
//     calm retry state below (we never bounce the user to another
//     surface: Operational Base must never open automatically).
//   • WELCOME_ESCAPE_DELAY_MS — after this long on the spinner, reveal
//     a manual escape to the Dashboard. Fast (cached) loads never hit
//     it, so there is no flash in the common case.
const WELCOME_FETCH_TIMEOUT_MS = 12000;
const WELCOME_ESCAPE_DELAY_MS = 3000;

// =====================================================================
//                          Event renderer map
// =====================================================================
const renderEvent = (event, ctx) => {
    switch (event.type) {
        case 'value_generated':       return <MetricCard key="value" event={event} ctx={ctx} tone="green" testId="welcome-card-value" />;
        case 'taps':                  return <MetricCard key="taps" event={event} ctx={ctx} tone="blue" testId="welcome-card-taps" />;
        case 'new_reviews':           return <MetricCard key="reviews" event={event} ctx={ctx} tone="yellow" testId="welcome-card-reviews" />;
        case 'feedback_messages':     return <MetricCard key="feedback" event={event} ctx={ctx} tone="violet" testId="welcome-card-feedback" />;
        case 'current_rating_snapshot': return <MetricCard key="rating-snap" event={event} ctx={ctx} tone="yellow" testId="welcome-card-rating-snapshot" />;
        case 'top_performer':         return <InsightCard key="top" event={event} ctx={ctx} kind="top_performer" testId="welcome-card-top-performer" />;
        case 'top_organization':      return <InsightCard key="top-org" event={event} ctx={ctx} kind="top_organization" testId="welcome-card-top-organization" />;
        case 'best_employee':         return <InsightCard key="employee" event={event} ctx={ctx} kind="best_employee" testId="welcome-card-best-employee" />;
        case 'biggest_improvement':   return <InsightCard key="improve" event={event} ctx={ctx} kind="biggest_improvement" testId="welcome-card-biggest-improvement" />;
        case 'review_growth':         return <InsightCard key="growth" event={event} ctx={ctx} kind="review_growth" testId="welcome-card-review-growth" />;
        // v1.3 — Morning Brief Positive Executive Refresh
        case 'month_to_date_summary': return <MonthToDateCard key="mtd" event={event} ctx={ctx} testId="welcome-card-mtd-summary" />;
        case 'ecosystem_highlights':  return <EcosystemHighlightsCard key="highlights" event={event} ctx={ctx} testId="welcome-card-ecosystem-highlights" />;
        // `needs_attention` intentionally removed in v1.3 — operational
        // issues belong inside Operational Base. If a stale payload still
        // carries one we silently drop it rather than render a warning
        // state on the celebratory Morning Brief.
        case 'needs_attention':       return null;
        case 'community_tip':         return <CommunityTipStrip key="tip" event={event} ctx={ctx} />;
        case 'quiet_day':             return <QuietDayCard key="quiet" event={event} ctx={ctx} />;
        case 'portfolio_placeholder': return <PortfolioPlaceholderCard key="portfolio" event={event} ctx={ctx} />;
        default: return null;
    }
};

// =====================================================================
//                              Main page
// =====================================================================
export default function Welcome() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, loading: authLoading, user, setPreferredLanguage } = useAuth();

    const isDemo = searchParams.get('demo') === 'true';

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [prefOverride, setPrefOverride] = useState(null);
    const [prefSaving, setPrefSaving] = useState(false);
    // Reveals a manual escape on the loading spinner if the briefing
    // takes too long — see WELCOME_ESCAPE_DELAY_MS.
    const [showEscape, setShowEscape] = useState(false);
    // Bumped by the Retry button in the error state below.
    const [reloadKey, setReloadKey] = useState(0);
    // P_LANGUAGE_SELF_SERVICE_1.0 — local saving flag for the
    // language dropdown. The active language always comes from the
    // shared auth/user record (server-persisted).
    const [langSaving, setLangSaving] = useState(false);
    const currentLang = user?.preferred_language || i18n.language || 'en';

    const prefEnabled = prefOverride !== null
        ? prefOverride
        : (user?.daily_review_enabled !== false);

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        let cancelled = false;
        // AbortController + timeout so a slow/hung daily-review can never
        // leave the user staring at a spinner with no way forward.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), WELCOME_FETCH_TIMEOUT_MS);
        (async () => {
            try {
                // Demo mode bypasses the welcomeAPI helper so the query
                // param round-trips intact and the dismiss/seen side-
                // effect is not recorded (we want to be able to revisit
                // demo mode freely while iterating on visuals).
                const tz = (() => {
                    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'; }
                    catch { return 'America/Toronto'; }
                })();
                const url = isDemo
                    ? `/welcome/daily-review?tz=${encodeURIComponent(tz)}&demo=true`
                    : `/welcome/daily-review?tz=${encodeURIComponent(tz)}`;
                const res = await api.get(url, { signal: controller.signal });
                if (!cancelled) {
                    setData(res.data);
                    // P_USER_WELCOME_DISPATCH_2.0 — mark seen on first
                    // mount. Idempotent on the server; safe to fire on
                    // every render.
                    const did = res.data?.dispatch_id;
                    if (did && !isDemo) {
                        try { await welcomeAPI.markSeen(did); } catch { /* idempotent */ }
                    }
                }
            } catch (e) {
                // Timeout-abort or any network/API failure → render the
                // calm retry state. We never redirect anywhere.
                if (!cancelled) {
                    const status = e?.response?.status;
                    setError(status ? `${status}` : (e?.message || 'failed'));
                }
            } finally {
                clearTimeout(timeoutId);
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; clearTimeout(timeoutId); controller.abort(); };
    }, [isAuthenticated, isDemo, reloadKey]);

    // Reveal the spinner escape hatch only after the grace period, so a
    // normal fast (cached) load never flashes it.
    useEffect(() => {
        const stillLoading = authLoading || loading || !data;
        if (!stillLoading) { setShowEscape(false); return undefined; }
        const id = setTimeout(() => setShowEscape(true), WELCOME_ESCAPE_DELAY_MS);
        return () => clearTimeout(id);
    }, [authLoading, loading, data]);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) navigate('/login', { replace: true });
    }, [authLoading, isAuthenticated, navigate]);

    const retry = () => {
        setError(null);
        setData(null);
        setLoading(true);
        setReloadKey((k) => k + 1);
    };

    const goWith = (path) => async () => {
        // P_USER_WELCOME_DISPATCH_2.0 — pressing any CTA (Got it /
        // Understood / Dismiss) counts as acknowledgement.
        const did = data?.dispatch_id;
        try {
            if (!isDemo) {
                if (did) await welcomeAPI.acknowledge(did);
                await welcomeAPI.dismiss();
            }
        } catch { /* idempotent */ }
        navigate(path);
    };

    const togglePreference = async () => {
        const next = !prefEnabled;
        setPrefSaving(true);
        try {
            await preferencesAPI.update({ daily_review_enabled: next });
            setPrefOverride(next);
            toast.success(t('welcome.preferences.dailyReview.saved'));
        } catch {
            toast.error(t('common.error', 'Something went wrong'));
        } finally {
            setPrefSaving(false);
        }
    };

    // P_LANGUAGE_SELF_SERVICE_1.0 — language selector in the welcome
    // footer. Uses the shared AuthContext.setPreferredLanguage which
    // persists to users.preferred_language and switches the active
    // i18n locale so the page reflects the change immediately.
    // Historical welcome dispatches are NOT mutated — only future
    // dispatches will be rendered in the new language.
    const onLanguageChange = async (next) => {
        if (!next || next === currentLang) return;
        setLangSaving(true);
        try {
            await setPreferredLanguage(next);
            toast.success(t('welcome.preferences.language.saved'));
        } catch {
            toast.error(t('common.error', 'Something went wrong'));
        } finally {
            setLangSaving(false);
        }
    };

    const obVisible = canSeeOperationalBase(user);
    const escapePath = obVisible ? '/operational-base' : '/dashboard';
    const escapeLabel = obVisible ? t('welcome.skipLink') : t('welcome.skipToDashboard');

    // Calm failure — the briefing stays put and offers Retry / Dashboard.
    // Operational Base is never opened automatically for anyone.
    if (!authLoading && isAuthenticated && error && !loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center bg-slate-950" data-testid="welcome-error">
                <AlertTriangle className="w-10 h-10 text-amber-400" />
                <div className="space-y-2 max-w-md">
                    <h1 className="text-2xl font-semibold text-white" data-testid="welcome-error-title">
                        {t('welcome.error.title')}
                    </h1>
                    <p className="text-sm text-slate-400" data-testid="welcome-error-body">
                        {t('welcome.error.body')}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                    <button
                        type="button"
                        onClick={retry}
                        data-testid="welcome-error-retry-btn"
                        className="px-5 py-2.5 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium transition-colors"
                    >
                        {t('welcome.error.retry')}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard', { replace: true })}
                        data-testid="welcome-error-dashboard-btn"
                        className="px-5 py-2.5 rounded-full border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-medium transition-colors"
                    >
                        {t('welcome.error.goDashboard')}
                    </button>
                </div>
            </div>
        );
    }

    if (authLoading || loading || !isAuthenticated || !data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-slate-950" data-testid="welcome-loading">
                <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
                {showEscape && (
                    <button
                        type="button"
                        onClick={() => navigate(escapePath, { replace: true })}
                        data-testid="welcome-loading-skip"
                        className="text-sm text-slate-400 hover:text-white underline underline-offset-4 inline-flex items-center gap-1 transition-colors"
                    >
                        {escapeLabel} <ArrowRight className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    }

    const ctx = { t, lang: i18n.language, i18n };
    const greeting = t(`welcome.greeting.${data.greeting_key || 'morning'}`);
    const isPlatform = data.scope.kind === 'platform';
    const isPortfolio = data.scope.kind === 'portfolio' || isPlatform;
    const title = isPlatform
        ? t('welcome.dailyPlatformReview')
        : (data.scope.kind === 'portfolio' ? t('welcome.dailyPortfolioReview') : t('welcome.dailyBusinessReview'));
    const heroSubtitle = isPlatform
        ? t('welcome.platform.heroSubtitle')
        : (data.scope.kind === 'portfolio'
            ? t('welcome.portfolio.heroSubtitle')
            : t('welcome.business.heroSubtitle', { location: data.scope.location_name || '' }));

    // Date subline — "Wednesday • June 17" in the user's locale + tz
    // (which we received in the payload as `today_local` + `timezone`).
    const dateLine = (() => {
        try {
            const d = data.today_local ? new Date(`${data.today_local}T12:00:00Z`) : new Date();
            return new Intl.DateTimeFormat(i18n.language, {
                weekday: 'long', month: 'long', day: 'numeric',
            }).format(d).replace(',', ' •');
        } catch {
            return '';
        }
    })();

    // Split events: metric tiles (top row) vs insight cards (lower row)
    // vs auxiliary (quiet day / placeholder / announcement / community tip).
    // v1.3 — Morning Brief Positive Executive Refresh:
    //  * Top row exactly 3 tiles: Customer Engagement / Reputation / Estimated Value Generated.
    //    (feedback_messages dropped here so the third slot is `value_generated`.)
    //  * Bottom row exactly 3 tiles: Top Org / Month To Date / Ecosystem Highlights.
    //    `needs_attention` dropped — operational alerts belong in Operational Base.
    const metricEvents = (data.events || []).filter((e) => ['taps', 'new_reviews', 'current_rating_snapshot', 'value_generated'].includes(e.type));
    const insightEvents = (data.events || []).filter((e) => ['top_performer', 'top_organization', 'best_employee', 'biggest_improvement', 'review_growth', 'month_to_date_summary', 'ecosystem_highlights'].includes(e.type));
    const auxEvents = (data.events || []).filter((e) => ['quiet_day', 'portfolio_placeholder'].includes(e.type));
    const announcement = (data.events || []).find((e) => e.type === 'announcement');
    const communityTip = (data.events || []).find((e) => e.type === 'community_tip');

    return (
        <div className="mission-control min-h-screen relative overflow-hidden" data-testid="welcome-page" style={{ background: 'var(--mc-bg-base)', color: 'var(--mc-text-main)' }}>
            {/* Mission Control design tokens live in
              * /app/frontend/src/styles/mission-control-tokens.css and are
              * imported globally via index.css. Edit THAT file to retune
              * the platform's visual language — never redeclare tokens here. */}

            {/* Cosmic background — orbital glow + radial gradient */}
            <CosmicBackdrop />

            <div className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-8 sm:pt-12 pb-16">
                {/* Top row */}
                <div className="flex items-center justify-between mb-10 sm:mb-14">
                    <div className="flex items-center gap-2 font-medium text-xs sm:text-sm tracking-[0.18em] uppercase" style={{ color: 'var(--mc-glow-right)' }}>
                        <Sparkles className="w-4 h-4" />
                        <span>{title}</span>
                    </div>
                    <button
                        type="button"
                        onClick={goWith(obVisible ? '/operational-base' : '/dashboard')}
                        className="text-sm underline underline-offset-4 transition-colors inline-flex items-center gap-1"
                        style={{ color: 'var(--mc-text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--mc-text-main)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--mc-text-secondary)'; }}
                        data-testid="welcome-skip-link"
                    >
                        {escapeLabel} <ArrowRight className="w-3 h-3" />
                    </button>
                </div>

                {/* Hero */}
                <header className="mb-8 sm:mb-12 mc-anim-fade-up" style={{ animationDelay: '0ms' }}>
                    <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-tight" style={{ color: 'var(--mc-text-main)' }} data-testid="welcome-greeting">
                        {greeting}{data.user_name ? `, ${data.user_name}` : ''}
                        <span className="inline-block align-middle ml-2"> 👋</span>
                    </h1>
                    {dateLine && (
                        <p className="mt-4 text-sm sm:text-base font-medium tracking-wide" style={{ color: 'var(--mc-glow-right)' }} data-testid="welcome-date-line">
                            {dateLine}
                        </p>
                    )}
                    {heroSubtitle && (
                        <p className="mt-2 text-base sm:text-lg" style={{ color: 'var(--mc-text-body)' }} data-testid="welcome-subtitle">
                            {heroSubtitle}
                        </p>
                    )}
                </header>

                {/* Aux states (kept as escape hatches; demo path no longer triggers these) */}
                {auxEvents.length > 0 && (
                    <div className="space-y-6 mb-8">
                        {auxEvents.map((e) => renderEvent(e, ctx))}
                    </div>
                )}

                {/* Hero "Portfolio/Platform Review" panel with brighter blue accent border */}
                {(metricEvents.length > 0 || insightEvents.length > 0) && (
                    <div className="mc-anim-fade-up" style={{ animationDelay: '120ms' }}>
                        <HeroPanel ctx={ctx} isPlatform={isPlatform} isPortfolio={isPortfolio} metrics={metricEvents} insights={insightEvents} />
                    </div>
                )}

                {/* Announcement strip — fades in last */}
                {announcement && (
                    <div className="mc-anim-fade" style={{ animationDelay: '780ms' }}>
                        <AnnouncementStrip event={announcement} ctx={ctx} />
                    </div>
                )}

                {/* Community tip — single-location only, fades in last */}
                {communityTip && (
                    <div className="mc-anim-fade" style={{ animationDelay: '780ms' }}>
                        <CommunityTipStrip event={communityTip} ctx={ctx} />
                    </div>
                )}

                {/* CTA buttons */}
                <div className={`mt-10 sm:mt-14 grid grid-cols-1 ${obVisible ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3 sm:gap-4 mc-anim-fade`} style={{ animationDelay: '900ms' }} data-testid="welcome-cta-strip">
                    <CtaButton
                        onClick={goWith('/analytics')}
                        tone="blue"
                        icon={<BarChart3 className="w-5 h-5" />}
                        title={t('welcome.cta.analytics.title')}
                        subtitle={t('welcome.cta.analytics.subtitle')}
                        testId="welcome-view-analytics-btn"
                    />
                    <CtaButton
                        onClick={goWith('/dashboard')}
                        tone="orange"
                        icon={<Home className="w-5 h-5" />}
                        title={t('welcome.cta.home.title')}
                        subtitle={t('welcome.cta.home.subtitle')}
                        testId="welcome-home-dashboard-btn"
                    />
                    {obVisible && (
                    <CtaButton
                        onClick={goWith('/operational-base')}
                        tone="green"
                        icon={<Rocket className="w-5 h-5" />}
                        title={t('welcome.cta.ob.title')}
                        subtitle={t('welcome.cta.ob.subtitle')}
                        testId="welcome-continue-to-ob-btn"
                    />
                    )}
                </div>

                {/* Preference toggle + language selector */}
                <div className="mt-10 sm:mt-14 flex flex-col items-center gap-4" data-testid="welcome-preference-toggle-wrap">
                    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                        <button
                            id="daily-review-toggle"
                            type="button"
                            role="switch"
                            aria-checked={prefEnabled}
                            disabled={prefSaving}
                            onClick={togglePreference}
                            data-testid="welcome-preference-toggle"
                            className="inline-flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors"
                        >
                            <span
                                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                    prefEnabled ? 'bg-sky-500' : 'bg-slate-700'
                                } ${prefSaving ? 'opacity-50' : ''}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out ${
                                        prefEnabled ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </span>
                            <label htmlFor="daily-review-toggle" className="cursor-pointer">
                                {prefEnabled ? '🔕 ' : '🔔 '}{t('welcome.preferences.dailyReview.label')}
                            </label>
                        </button>

                        {/* P_LANGUAGE_SELF_SERVICE_1.0 — user-driven
                            language self-selection. Persists to
                            users.preferred_language; affects FUTURE
                            welcome dispatches only — historical rows
                            keep their immutable language_delivered. */}
                        <div className="inline-flex items-center gap-2 text-sm text-slate-300" data-testid="welcome-language-selector-wrap">
                            <span aria-hidden="true">🌎</span>
                            <label htmlFor="welcome-language-select" className="sr-only sm:not-sr-only">
                                {t('welcome.preferences.language.label')}:
                            </label>
                            <select
                                id="welcome-language-select"
                                disabled={langSaving}
                                value={currentLang}
                                onChange={(e) => onLanguageChange(e.target.value)}
                                data-testid="welcome-language-select"
                                className={`bg-slate-900/60 border border-slate-700 rounded-md px-2 py-1 text-sm text-slate-200 hover:border-slate-500 focus:border-sky-500 focus:outline-none transition-colors ${langSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <option value="en">{t('welcome.preferences.language.options.en')}</option>
                                <option value="fr">{t('welcome.preferences.language.options.fr')}</option>
                                <option value="es">{t('welcome.preferences.language.options.es')}</option>
                            </select>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">{t('welcome.preferences.dailyReview.helper')}</p>
                </div>
            </div>
        </div>
    );
}

// =====================================================================
//                          Layout primitives
// =====================================================================
const CosmicBackdrop = () => (
    <>
        {/* Deep space base + outer-frame side glows per design tokens.
          * Left = electric blue, right = warm orange. Bottom emerald is
          * derived from the green CTA glow token so the whole stage
          * coheres with the button color hierarchy. */}
        <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, var(--mc-bg-base) 0%, var(--mc-bg-secondary) 50%, var(--mc-bg-base) 100%)' }}
            aria-hidden="true"
        />
        <div
            className="absolute -top-32 -left-40 w-[640px] h-[640px] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(closest-side, var(--mc-glow-left) 0%, transparent 70%)', opacity: 0.30 }}
            aria-hidden="true"
        />
        <div
            className="absolute -top-32 -right-32 w-[640px] h-[640px] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(closest-side, var(--mc-glow-right) 0%, transparent 70%)', opacity: 0.28 }}
            aria-hidden="true"
        />
        <div
            className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(closest-side, var(--mc-btn-green-icon) 0%, transparent 70%)', opacity: 0.18 }}
            aria-hidden="true"
        />
        {/* Horizon divider under the eyebrow */}
        <div
            className="absolute top-32 inset-x-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, var(--mc-glow-left), transparent)', opacity: 0.5 }}
            aria-hidden="true"
        />
    </>
);

const HeroPanel = ({ ctx, isPlatform, isPortfolio, metrics, insights }) => (
    <div
        className="relative rounded-2xl p-6 sm:p-8 backdrop-blur-sm"
        style={{
            background: 'var(--mc-card-bg)',
            border: '1px solid var(--mc-card-border)',
            boxShadow: '-1px 0 0 var(--mc-glow-left), 1px 0 0 var(--mc-glow-right), 0 0 60px -10px rgba(45, 140, 255, 0.30), 0 0 80px -20px rgba(255, 154, 31, 0.20)',
        }}
        data-testid="welcome-hero-panel"
    >
        {/* Panel header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
            <div className="flex items-center gap-3">
                <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(45, 140, 255, 0.18)', border: '1px solid var(--mc-glow-left)' }}
                >
                    <Globe2 className="w-5 h-5" style={{ color: 'var(--mc-glow-left)' }} />
                </div>
                <div>
                    <div className="text-base sm:text-lg font-semibold tracking-wide" style={{ color: 'var(--mc-text-main)' }}>
                        {isPlatform
                            ? ctx.t('welcome.panel.portfolioTitle')
                            : (isPortfolio ? ctx.t('welcome.panel.portfolioTitle') : ctx.t('welcome.panel.businessTitle'))}
                    </div>
                    {/* v1.3 — Two-line subtitle: top tiles show yesterday,
                        bottom tiles show month-to-date. The split is
                        baked into the copy so the operator always knows
                        which time slice they're reading. */}
                    <div className="text-xs leading-snug" style={{ color: 'var(--mc-text-secondary)' }} data-testid="welcome-panel-subtitle">
                        <span className="block">{ctx.t('welcome.panel.subtitle')}</span>
                        <span className="block">{ctx.t('welcome.panel.subtitleMonth')}</span>
                    </div>
                </div>
            </div>
            <div
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full self-start sm:self-auto"
                style={{ color: 'var(--mc-glow-right)', background: 'rgba(255, 154, 31, 0.12)', border: '1px solid rgba(255, 154, 31, 0.45)' }}
            >
                <Clock className="w-3.5 h-3.5" />
                <span>{ctx.t('welcome.panel.refresh')}</span>
            </div>
        </div>

        {/* Metric tiles — staggered fade-up.
            v1.3 — Reduced from 4 to exactly 3 columns to match the
            executive-refresh layout: Customer Engagement / Reputation /
            Estimated Value Generated. */}
        {metrics.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {metrics.map((e, idx) => (
                    <div key={`m-${e.type}`} className="mc-anim-fade-up" style={{ animationDelay: `${240 + idx * 60}ms` }}>
                        {renderEvent(e, ctx)}
                    </div>
                ))}
            </div>
        )}

        {/* Insight tiles — slide up after metrics */}
        {insights.length > 0 && (
            <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-6"
                style={{ borderTop: '1px solid var(--mc-divider)' }}
            >
                {insights.map((e, idx) => (
                    <div key={`i-${e.type}`} className="mc-anim-fade-up" style={{ animationDelay: `${540 + idx * 80}ms` }}>
                        {renderEvent(e, ctx)}
                    </div>
                ))}
            </div>
        )}
    </div>
);

// =====================================================================
//                              Metric tiles
// =====================================================================
const TONE = {
    green:  { accent: 'var(--mc-accent-value)' },
    blue:   { accent: 'var(--mc-accent-taps)' },
    yellow: { accent: 'var(--mc-accent-rep)' },
    violet: { accent: 'var(--mc-accent-recv)' },
};

const ICON_FOR_METRIC = {
    value_generated: <DollarSign className="w-5 h-5" />,
    taps: <Users className="w-5 h-5" />,
    new_reviews: <Star className="w-5 h-5" />,
    current_rating_snapshot: <Star className="w-5 h-5" />,
    feedback_messages: <MessageSquare className="w-5 h-5" />,
};

const LABEL_KEY_FOR_METRIC = {
    value_generated: 'welcome.metrics.value.eyebrow',
    taps: 'welcome.metrics.taps.eyebrow',
    new_reviews: 'welcome.metrics.reviews.eyebrow',
    current_rating_snapshot: 'welcome.metrics.ratingSnapshot.eyebrow',
    feedback_messages: 'welcome.metrics.feedback.eyebrow',
};

const MetricCard = ({ event, ctx, tone, testId }) => {
    const accent = (TONE[tone] || TONE.blue).accent;
    const delta = event.delta_pct;
    const hasDelta = typeof delta === 'number';
    const deltaPositive = hasDelta && delta >= 0;
    const formatted = formatMetricValue(event, ctx);
    return (
        <div
            className="relative rounded-xl p-4 sm:p-5 flex flex-col gap-3 transition-transform duration-200 hover:-translate-y-0.5"
            style={{ background: 'var(--mc-card-bg)', border: '1px solid var(--mc-card-border)' }}
            data-testid={testId}
        >
            <div className="flex items-center gap-2">
                <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}55` }}
                >
                    {ICON_FOR_METRIC[event.type]}
                </div>
                <div className="text-[10px] sm:text-[11px] font-semibold tracking-[0.14em] uppercase" style={{ color: accent }}>
                    {ctx.t(LABEL_KEY_FOR_METRIC[event.type])}
                </div>
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: 'var(--mc-text-main)' }} data-testid={`${testId}-value`}>{formatted.main}</span>
                {formatted.badge && (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium"
                        style={{ background: 'rgba(255, 201, 77, 0.15)', color: 'var(--mc-accent-rep)', border: '1px solid rgba(255, 201, 77, 0.40)' }}
                    >
                        {formatted.badge}
                    </span>
                )}
            </div>
            {hasDelta ? (
                <div
                    className="inline-flex items-center gap-1 text-xs"
                    style={{ color: deltaPositive ? 'var(--mc-accent-value)' : '#FF6B7A' }}
                >
                    {deltaPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                    <span>{Math.abs(delta)}% {ctx.t(event.period === 'month_to_date' ? 'welcome.metrics.vsLastMonth' : 'welcome.metrics.vsYesterday')}</span>
                </div>
            ) : formatted.footnote ? (
                // P_PHASE_4_8 — Case B growth-since-baseline footnote
                <div className="text-xs" style={{ color: 'var(--mc-text-secondary)' }} data-testid={`${testId}-footnote`}>
                    {formatted.footnote}
                </div>
            ) : (
                <div className="text-xs" style={{ color: 'var(--mc-text-secondary)' }}>
                    {ctx.t(event.period === 'month_to_date' ? 'welcome.metrics.monthToDate' : 'welcome.metrics.last24h')}
                </div>
            )}
        </div>
    );
};

/** Returns {main, badge} for the metric value display. */
function formatMetricValue(event, ctx) {
    if (event.type === 'value_generated') {
        const formatted = new Intl.NumberFormat(ctx.lang, {
            style: 'currency',
            currency: event.currency || 'CAD',
            maximumFractionDigits: 0,
            currencyDisplay: 'narrowSymbol',
        }).format(event.value || 0);
        return { main: formatted };
    }
    if (event.type === 'new_reviews') {
        // P_PHASE_4_8_REPUTATION_BASELINE_FALLBACK — case-aware render.
        // Case A → parity numbers (legacy behaviour). Case B → manual
        // snapshot + growth-since-baseline. Case C → "Not configured".
        if (event.case === 'B') {
            const reviewsVal = (event.display_reviews_count ?? 0).toLocaleString(ctx.lang);
            const ratingVal = event.display_avg_rating;
            const growth = event.growth_since_baseline;
            const badge = ratingVal != null
                ? ctx.t('welcome.metrics.reviews.ratingBadge', { rating: ratingVal })
                : null;
            const growthLabel = growth === null || growth === undefined
                ? null
                : (growth >= 0
                    ? `+${growth.toLocaleString(ctx.lang)} since baseline`
                    : `${growth.toLocaleString(ctx.lang)} since baseline`);
            return { main: reviewsVal, badge, footnote: growthLabel };
        }
        if (event.case === 'C') {
            return { main: 'Not configured', badge: null };
        }
        // Case A (or legacy payload without `case`).
        const main = (event.count || 0).toLocaleString(ctx.lang);
        const badge = event.avg_rating != null ? ctx.t('welcome.metrics.reviews.ratingBadge', { rating: event.avg_rating }) : null;
        return { main, badge };
    }
    if (event.type === 'current_rating_snapshot') {
        return { main: `${event.avg_rating}★` };
    }
    const main = (event.count || 0).toLocaleString(ctx.lang);
    return { main };
}

// =====================================================================
//                            Insight tiles
// =====================================================================
const InsightCard = ({ event, ctx, kind, testId }) => {
    // Map insight kind → design token accent. Green family for top
    // performers, orange family for improvement signals, recovery red
    // for needs-attention. Aligned with the metric-card token system.
    const accent = (() => {
        switch (kind) {
            case 'top_performer':
            case 'top_organization':
            case 'best_employee':
                return 'var(--mc-accent-value)';
            case 'biggest_improvement':
            case 'review_growth':
                return 'var(--mc-glow-right)';
            case 'needs_attention':
                return '#FF6B7A'; // Calibrated alert red — matches negative delta color in metric cards.
            default:
                return 'var(--mc-text-body)';
        }
    })();
    const Icon = (() => {
        if (kind === 'needs_attention') return AlertTriangle;
        if (kind === 'biggest_improvement' || kind === 'review_growth') return TrendingUp;
        return Trophy;
    })();
    const detail = (() => {
        if (kind === 'top_performer' || kind === 'top_organization') {
            return ctx.t('welcome.insights.top_performer.detail', {
                taps: event.taps, rating: event.rating, value: event.value_delta,
                defaultValue: ctx.t(`welcome.insights.${kind}.detail`, { taps: event.taps, rating: event.rating, value: event.value_delta }),
            });
        }
        if (kind === 'best_employee')       return ctx.t('welcome.insights.best_employee.detail', { count: event.taps || 0 });
        if (kind === 'biggest_improvement') return ctx.t('welcome.insights.biggest_improvement.detail', { pct: event.tap_delta_pct });
        if (kind === 'review_growth')       return ctx.t('welcome.insights.review_growth.detail', { pct: event.delta_pct });
        if (kind === 'needs_attention') {
            // Phase 1b: needs_attention is a tagged union over three signals.
            // Each signal gets its own copy + variable set so the briefing
            // tells the operator exactly WHICH concern triggered.
            const signal = event.signal || 'unresolved_feedback';
            if (signal === 'rating_drop') {
                return ctx.t('welcome.insights.needs_attention.signal.rating_drop', {
                    current: event.current_rating, baseline: event.baseline_rating,
                });
            }
            if (signal === 'tap_drop') {
                return ctx.t('welcome.insights.needs_attention.signal.tap_drop', {
                    pct: event.drop_pct,
                });
            }
            return ctx.t('welcome.insights.needs_attention.signal.unresolved_feedback', {
                count: event.unresolved_feedback || 0,
            });
        }
        return '';
    })();
    // Runners-up subtitle for top performer/organization — Phase 1b
    // shows the podium #2/#3 without scaling into a sprawling leaderboard.
    // Mission Control is a briefing, not a report; three names is enough.
    const runnersUp = (kind === 'top_performer' || kind === 'top_organization') && Array.isArray(event.runners_up) && event.runners_up.length > 0
        ? event.runners_up
        : null;
    const runnersUpLine = runnersUp
        ? (runnersUp.length >= 2
            ? ctx.t('welcome.insights.top_performer.runnersUpPair', {
                name2: runnersUp[0].name,
                name3: runnersUp[1].name,
            })
            : ctx.t('welcome.insights.top_performer.runnersUp', {
                count: runnersUp[0].taps || 0,
                name: runnersUp[0].name,
            }))
        : null;
    return (
        <div className="flex items-center gap-4 p-2" data-testid={testId}>
            <div
                className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${accent}1F`, color: accent, border: `1px solid ${accent}55` }}
            >
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] sm:text-[11px] font-semibold tracking-[0.14em] uppercase" style={{ color: accent }}>
                    {ctx.t(`welcome.insights.${kind}.eyebrow`)}
                </div>
                <div className="text-lg sm:text-xl font-semibold truncate" style={{ color: 'var(--mc-text-main)' }}>{event.name}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--mc-text-secondary)' }}>{detail}</div>
                {runnersUpLine && (
                    <div
                        className="text-[11px] mt-1 truncate"
                        style={{ color: 'var(--mc-text-secondary)', opacity: 0.75 }}
                        data-testid={`${testId}-runners-up`}
                    >
                        {runnersUpLine}
                    </div>
                )}
            </div>
        </div>
    );
};

// =====================================================================
//                          Announcement strip
//
// Renders BOTH legacy demo announcements (i18n keys) AND v4.37 Phase 2
// broadcasts (raw trilingual objects + optional CTA). Branching key:
// the presence of `event.title` (string object) vs `event.title_key`
// (i18n string). Broadcasts also carry `broadcast_id` so dismissal
// hits the backend.
// =====================================================================
const _pickLocale = (obj, lang) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return (obj[lang] && obj[lang].trim()) ? obj[lang] : (obj.en || '');
};

const _broadcastPriorityFg = (p) =>
    p === 'critical' ? '#FF4B57' : p === 'important' ? '#FFB347' : '#4AA3FF';

const AnnouncementStrip = ({ event, ctx }) => {
    const isBroadcast = !!event.title && typeof event.title === 'object';
    const lang = ctx.i18n?.language || 'en';
    const titleText = isBroadcast
        ? _pickLocale(event.title, lang)
        : ctx.t(event.title_key || 'welcome.announcement.fallbackTitle');
    const subtitleText = isBroadcast
        ? _pickLocale(event.body, lang)
        : (event.subtitle_key ? ctx.t(event.subtitle_key) : null);
    const ctaText = isBroadcast
        ? (event.cta ? _pickLocale(event.cta.label, lang) : null)
        : ctx.t(event.cta_key || 'welcome.announcement.fallbackCta');
    const ctaUrl = isBroadcast ? (event.cta?.url || null) : null;
    const accent = isBroadcast ? _broadcastPriorityFg(event.priority) : null;

    const handleCta = () => {
        if (!ctaUrl) return;
        if (ctaUrl.startsWith('/')) window.location.assign(ctaUrl);
        else window.open(ctaUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDismiss = async () => {
        if (!event.broadcast_id) return;
        try {
            const { broadcastsAPI } = await import('../lib/api');
            await broadcastsAPI.dismiss(event.broadcast_id);
            // Force a refresh of the Mission Control payload so the
            // dismissed announcement disappears immediately.
            window.location.reload();
        } catch (_e) { /* swallow */ }
    };

    return (
        <div
            className="mt-6 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative"
            style={{
                background: accent
                    ? `linear-gradient(90deg, ${accent}26 0%, ${accent}14 100%)`
                    : 'linear-gradient(90deg, var(--mc-ann-grad-left) 0%, var(--mc-ann-grad-center) 50%, var(--mc-ann-grad-right) 100%)',
                border: accent ? `1px solid ${accent}55` : '1px solid var(--mc-card-border)',
            }}
            data-testid="welcome-card-announcement"
            data-broadcast-id={event.broadcast_id || undefined}
            data-priority={event.priority || undefined}
        >
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{
                        background: accent ? `${accent}33` : 'rgba(75, 131, 217, 0.30)',
                        color: '#FFFFFF',
                        border: accent ? `1px solid ${accent}88` : '1px solid rgba(75, 131, 217, 0.65)',
                    }}
                >
                    <Megaphone className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase mb-1" style={{ color: accent || '#B5CAE8' }}>
                        {isBroadcast
                            ? ctx.t(`broadcasts.priority_eyebrow.${event.priority || 'normal'}`) + ' · ' + ctx.t('broadcasts.announcement_eyebrow')
                            : ctx.t('welcome.announcement.eyebrow')}
                    </div>
                    <div className="text-base sm:text-lg font-semibold" style={{ color: 'var(--mc-text-main)' }}>{titleText}</div>
                    {subtitleText && (
                        <div className="text-sm mt-0.5 whitespace-pre-wrap" style={{ color: '#B5CAE8' }}>{subtitleText}</div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto">
                {ctaText && (
                    <button
                        type="button"
                        onClick={handleCta}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        style={{
                            background: accent || 'var(--mc-ann-btn-bg)',
                            color: accent ? '#020817' : '#FFFFFF',
                            border: '1px solid rgba(255,255,255,0.20)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.10)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
                        data-testid="welcome-announcement-cta"
                    >
                        {ctaText}
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                )}
                {event.broadcast_id && (
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="text-white/50 hover:text-white transition-colors p-1"
                        aria-label={ctx.t('broadcasts.dismiss')}
                        data-testid="welcome-announcement-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};


// =====================================================================
//   v1.3 — Morning Brief Positive Executive Refresh — new tile components
// =====================================================================

/**
 * MonthToDateCard — bottom-row CENTER tile.
 *
 * Displays month-to-date counts of engagements, reviews generated,
 * and guest recoveries with a "↑ X% vs last month" footer using the
 * apples-to-apples same-calendar-period baseline.
 *
 * Per spec, Guest Recoveries appear ONLY on this card — nowhere else
 * on the page.
 */
const MonthToDateCard = ({ event, ctx, testId }) => {
    const delta = event.mom_delta_pct;
    const hasDelta = typeof delta === 'number';
    const positive = hasDelta && delta >= 0;
    const lang = ctx.lang;
    const fmt = (n) => (n || 0).toLocaleString(lang);
    return (
        <div
            className="relative rounded-xl p-4 sm:p-5 flex flex-col gap-3 transition-transform duration-200 hover:-translate-y-0.5"
            style={{ background: 'var(--mc-card-bg)', border: '1px solid var(--mc-card-border)' }}
            data-testid={testId}
            data-insight-id="welcome.executive.month_to_date"
        >
            <div className="text-[10px] sm:text-[11px] font-semibold tracking-[0.14em] uppercase" style={{ color: 'var(--mc-glow-left)' }}>
                {ctx.t('welcome.executive.mtd.eyebrow')}
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--mc-text-main)' }}>
                    <span>👥</span>
                    <span className="font-semibold tabular-nums" data-testid={`${testId}-engagements`}>{fmt(event.engagements)}</span>
                    <span className="text-xs" style={{ color: 'var(--mc-text-secondary)' }}>
                        {ctx.t('welcome.executive.mtd.engagements')}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--mc-text-main)' }}>
                    <span>⭐</span>
                    {/* P_PHASE_4_8 — Case-aware MTD reviews tile. */}
                    {event.reputation_case === 'B' ? (
                        <>
                            <span className="font-semibold tabular-nums" data-testid={`${testId}-reviews`}>
                                {fmt(event.reputation_display_reviews_count ?? 0)}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--mc-text-secondary)' }}>
                                {ctx.t('welcome.executive.mtd.reviews')}
                            </span>
                            {typeof event.reputation_growth_since_baseline === 'number' && (
                                <span className="text-xs" style={{ color: 'var(--mc-text-secondary)' }} data-testid={`${testId}-growth`}>
                                    ({event.reputation_growth_since_baseline >= 0 ? '+' : ''}{fmt(event.reputation_growth_since_baseline)} since baseline)
                                </span>
                            )}
                        </>
                    ) : event.reputation_case === 'C' ? (
                        <>
                            <span className="font-semibold" data-testid={`${testId}-reviews`}>Not configured</span>
                        </>
                    ) : (
                        <>
                            <span className="font-semibold tabular-nums" data-testid={`${testId}-reviews`}>{fmt(event.reviews_generated)}</span>
                            <span className="text-xs" style={{ color: 'var(--mc-text-secondary)' }}>
                                {ctx.t('welcome.executive.mtd.reviews')}
                            </span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--mc-text-main)' }}>
                    <span>💬</span>
                    <span className="font-semibold tabular-nums" data-testid={`${testId}-recoveries`}>{fmt(event.guest_recoveries)}</span>
                    <span className="text-xs" style={{ color: 'var(--mc-text-secondary)' }}>
                        {ctx.t('welcome.executive.mtd.recoveries')}
                    </span>
                </div>
            </div>
            {hasDelta && (
                <div
                    className="inline-flex items-center gap-1 text-xs mt-1"
                    style={{ color: positive ? 'var(--mc-accent-value)' : '#FF6B7A' }}
                    data-testid={`${testId}-delta`}
                >
                    {positive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                    <span>{Math.abs(delta)}% {ctx.t('welcome.metrics.vsLastMonth')}</span>
                </div>
            )}
        </div>
    );
};


/**
 * EcosystemHighlightsCard — bottom-row RIGHT tile.
 *
 * Positive momentum ONLY (per spec). Renders up to four highlight
 * lines: value delta in dollars, average rating, best day this month,
 * and week-to-date vs same week last month. Any field that's missing
 * from the payload is silently skipped — never rendered as a warning
 * or empty state.
 */
const EcosystemHighlightsCard = ({ event, ctx, testId }) => {
    const lang = ctx.lang;
    const lines = [];
    if (typeof event.value_delta_dollars === 'number' && event.value_delta_dollars !== 0) {
        const sign = event.value_delta_dollars >= 0 ? '+' : '';
        const formatted = new Intl.NumberFormat(lang, {
            style: 'currency',
            currency: event.currency || 'CAD',
            maximumFractionDigits: 0,
            currencyDisplay: 'narrowSymbol',
        }).format(Math.abs(event.value_delta_dollars));
        lines.push({ icon: event.value_delta_dollars >= 0 ? '💰' : '📉', text: `${sign}${formatted} ${ctx.t('welcome.executive.highlights.vsLastMonth')}`, testid: `${testId}-value-delta` });
    }
    if (typeof event.avg_rating === 'number') {
        lines.push({ icon: '⭐', text: `${event.avg_rating} ${ctx.t('welcome.executive.highlights.avgRating')}`, testid: `${testId}-avg-rating` });
    }
    if (event.best_day_weekday) {
        // Translate weekday names through i18n so FR / ES read correctly.
        // Backend returns the English weekday name; we map via a fixed
        // key set already shipped in the locale bundles.
        const translatedDay = ctx.t(`welcome.executive.weekday.${event.best_day_weekday.toLowerCase()}`, event.best_day_weekday);
        lines.push({ icon: '📅', text: `${translatedDay} — ${ctx.t('welcome.executive.highlights.bestDay')}`, testid: `${testId}-best-day` });
    }
    if (typeof event.wtd_vs_last_month_pct === 'number') {
        const positive = event.wtd_vs_last_month_pct >= 0;
        const sign = positive ? '+' : '';
        lines.push({
            icon: '📈',
            text: `${sign}${event.wtd_vs_last_month_pct}% ${ctx.t('welcome.executive.highlights.weekVsLastMonth')}`,
            testid: `${testId}-wtd-delta`,
        });
    }
    if (lines.length === 0) return null;
    return (
        <div
            className="relative rounded-xl p-4 sm:p-5 flex flex-col gap-3 transition-transform duration-200 hover:-translate-y-0.5"
            style={{ background: 'var(--mc-card-bg)', border: '1px solid var(--mc-card-border)' }}
            data-testid={testId}
            data-insight-id="welcome.executive.ecosystem_highlights"
        >
            <div className="text-[10px] sm:text-[11px] font-semibold tracking-[0.14em] uppercase" style={{ color: 'var(--mc-glow-right)' }}>
                {ctx.t('welcome.executive.highlights.eyebrow')}
            </div>
            <ul className="flex flex-col gap-2">
                {lines.map((l) => (
                    <li key={l.testid} className="flex items-center gap-2 text-sm" style={{ color: 'var(--mc-text-main)' }} data-testid={l.testid}>
                        <span>{l.icon}</span>
                        <span>{l.text}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};


const CommunityTipStrip = ({ event, ctx }) => (
    <div
        className="mt-6 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        style={{
            background: `linear-gradient(90deg, ${cssBlend('var(--mc-btn-orange-bg)', 0.55)} 0%, ${cssBlend('var(--mc-btn-orange-hover)', 0.40)} 50%, ${cssBlend('var(--mc-btn-orange-border)', 0.20)} 100%)`,
            border: '1px solid var(--mc-card-border)',
        }}
        data-testid="welcome-card-community-tip"
    >
        <div className="flex items-start gap-3">
            <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'rgba(247, 169, 51, 0.25)', color: '#FFFFFF', border: '1px solid var(--mc-btn-orange-border)' }}
            >
                <Sparkles className="w-5 h-5" />
            </div>
            <div>
                <div className="text-[10px] font-semibold tracking-[0.14em] uppercase mb-1" style={{ color: 'var(--mc-btn-orange-subtitle)' }}>
                    {ctx.t('welcome.communityTip.aboveAverage.eyebrow')}
                </div>
                <div className="text-base sm:text-lg font-semibold" style={{ color: 'var(--mc-text-main)' }}>
                    {ctx.t(event.title_key || 'welcome.communityTip.aboveAverage.title')}
                </div>
                {event.subtitle_key && (
                    <div className="text-sm mt-0.5" style={{ color: 'var(--mc-btn-orange-subtitle)' }}>{ctx.t(event.subtitle_key)}</div>
                )}
            </div>
        </div>
    </div>
);

// CSS won't blend var() references in opacity directly, so we just
// pass through to a fallback rgba where needed. Kept tiny + inline.
function cssBlend(_token, opacity) {
    // The gradient acts on the underlying token at the listed opacity
    // via color-mix where supported. Browser fallback uses solid color.
    return `color-mix(in srgb, ${_token} ${Math.round(opacity * 100)}%, transparent)`;
}

// =====================================================================
//                              CTA buttons
// =====================================================================
const CtaButton = ({ onClick, tone, icon, title, subtitle, testId }) => {
    const subtitleColor = {
        blue:   'var(--mc-btn-blue-subtitle)',
        orange: 'var(--mc-btn-orange-subtitle)',
        green:  'var(--mc-btn-green-subtitle)',
    }[tone];
    const iconBg = {
        blue:   'var(--mc-btn-blue-icon)',
        orange: 'var(--mc-btn-orange-icon)',
        green:  'var(--mc-btn-green-icon)',
    }[tone];
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`group mc-cta mc-cta-${tone}`}
        >
            <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: iconBg, color: '#FFFFFF' }}
            >
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-base font-semibold" style={{ color: '#FFFFFF' }}>{title}</div>
                <div className="text-xs mt-0.5" style={{ color: subtitleColor }}>{subtitle}</div>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" style={{ color: 'rgba(255,255,255,0.65)' }} />
        </button>
    );
};

// =====================================================================
//                        Aux state cards (full-width)
// =====================================================================
const QuietDayCard = ({ event, ctx }) => (
    <div
        className="rounded-2xl p-8 sm:p-10 text-center bg-slate-900/40 ring-1 ring-slate-700/60"
        data-testid="welcome-card-quiet-day"
    >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-700/60 text-slate-300 mb-4">
            <TrendingUp className="w-6 h-6" />
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">{ctx.t('welcome.quietDay.title')}</h2>
        <p className="text-base text-slate-400 max-w-2xl mx-auto">
            {ctx.t('welcome.quietDay.message', { location: event.location_name || ctx.t('welcome.quietDay.fallbackLocation') })}
        </p>
    </div>
);

const PortfolioPlaceholderCard = ({ event, ctx }) => (
    <div
        className="rounded-2xl p-8 sm:p-10 text-center bg-gradient-to-br from-sky-950/40 to-amber-950/30 ring-1 ring-sky-400/30"
        data-testid="welcome-card-portfolio-placeholder"
    >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-sky-500/20 ring-1 ring-sky-400/40 text-sky-300 mb-4">
            <Sparkles className="w-6 h-6" />
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">
            {event.location_count > 0
                ? ctx.t('welcome.portfolio.comingSoon.title', { count: event.location_count })
                : ctx.t('welcome.portfolio.comingSoon.titleGeneric')}
        </h2>
        <p className="text-base text-slate-400 max-w-2xl mx-auto">{ctx.t('welcome.portfolio.comingSoon.message')}</p>
    </div>
);
